import type { TimelineAudioClip } from '../panels/timeline/types'
import type { AudioEditClip } from './audioEditTypes'

const MIN_DURATION_S = 0.1
export const MIN_AUDIO_EDIT_SPEED = 0.1

export function normalizeAudioSpeed(
  speed: number | null | undefined,
  minimumSpeed = MIN_AUDIO_EDIT_SPEED,
): number {
  const safeMinimum = Math.max(0, minimumSpeed)
  const numericSpeed = Number.isFinite(speed) ? Number(speed) : 1
  if (numericSpeed <= 0 && safeMinimum === 0) return 1
  return Math.max(safeMinimum, numericSpeed)
}

export function getAudioEditDurationS(
  clip: AudioEditClip,
  minimumSpeed = MIN_AUDIO_EDIT_SPEED,
): number {
  return (
    Math.max(0, clip.sourceOutS - clip.sourceInS) /
    normalizeAudioSpeed(clip.speed, minimumSpeed)
  )
}

export function getAudioEditTimelineEndS(clip: AudioEditClip): number {
  return clip.timelineStartS + getAudioEditDurationS(clip)
}

export function getAudioEditSourceTimeS(
  clip: AudioEditClip,
  playheadS: number,
  minimumSpeed = MIN_AUDIO_EDIT_SPEED,
): number {
  const elapsedS = Math.max(0, playheadS - clip.timelineStartS)
  return clip.sourceInS + elapsedS * normalizeAudioSpeed(clip.speed, minimumSpeed)
}

export function trimAudioEditLeftByTimelineDelta(
  clip: AudioEditClip,
  timelineDeltaS: number,
): AudioEditClip {
  const sourceDeltaS = timelineDeltaS * normalizeAudioSpeed(clip.speed)
  const maxSourceInS = clip.sourceOutS - MIN_DURATION_S
  const nextSourceInS = Math.max(0, Math.min(clip.sourceInS + sourceDeltaS, maxSourceInS))
  const actualSourceDeltaS = nextSourceInS - clip.sourceInS
  return {
    ...clip,
    sourceInS: nextSourceInS,
    timelineStartS: Math.max(
      0,
      clip.timelineStartS + actualSourceDeltaS / normalizeAudioSpeed(clip.speed),
    ),
  }
}

export function trimAudioEditRightByTimelineDelta(
  clip: AudioEditClip,
  timelineDeltaS: number,
): AudioEditClip {
  const sourceDeltaS = timelineDeltaS * normalizeAudioSpeed(clip.speed)
  const minSourceOutS = clip.sourceInS + MIN_DURATION_S
  return {
    ...clip,
    sourceOutS: Math.max(minSourceOutS, clip.sourceOutS + sourceDeltaS),
  }
}

export function setAudioEditSpeed(clip: AudioEditClip, speed: number): AudioEditClip {
  return { ...clip, speed: normalizeAudioSpeed(speed), preservePitch: true }
}

export function setAudioEditVolume(clip: AudioEditClip, volume: number): AudioEditClip {
  return { ...clip, volume: Math.max(0, Math.min(1, volume)) }
}

export function timelineAudioToEditClip(clip: TimelineAudioClip): AudioEditClip {
  return {
    id: clip.id,
    sourcePath: clip.sourcePath,
    timelineStartS: clip.startOffset,
    sourceInS: clip.trimStart,
    sourceOutS: Math.max(clip.trimStart, clip.originalDuration - clip.trimEnd),
    speed: normalizeAudioSpeed(clip.speed),
    volume: clip.volume,
    muted: clip.muted,
    preservePitch: true,
  }
}

export function editClipToTimelineAudio(
  editClip: AudioEditClip,
  original: TimelineAudioClip,
): TimelineAudioClip {
  const sourceOutS = Math.min(
    original.originalDuration,
    Math.max(editClip.sourceInS, editClip.sourceOutS),
  )
  const trimEnd = Math.max(0, original.originalDuration - sourceOutS)
  const normalizedEditClip = { ...editClip, sourceOutS }
  return {
    ...original,
    trimStart: Math.max(0, editClip.sourceInS),
    trimEnd,
    startOffset: Math.max(0, editClip.timelineStartS),
    speed: normalizeAudioSpeed(editClip.speed),
    volume: Math.max(0, Math.min(1, editClip.volume)),
    muted: editClip.muted,
    effectiveDuration: getAudioEditDurationS(normalizedEditClip),
  }
}
